import { useRef, useState } from 'react';
import { View } from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { BaseModalProps } from '@/contexts/ModalContext';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/drawer';
import { useTranslation } from 'react-i18next';

// Conservative delay covering the scanner modal's dismissal. iOS silently drops a
// modal presentation that overlaps another modal's dismissal, so the results
// drawer must open only after this one has closed.
const MODAL_TRANSITION_DELAY_MS = 300;

export interface MealScannerModalProps extends BaseModalProps {
  /**
   * Called when a barcode is scanned
   */
  onBarcodeScanned?: (barcode: string) => void;
  /**
   * Portal host name for custom dialog placement
   */
  portalHost?: string;
}

export const MealScannerModal = ({
  onClose,
  onBarcodeScanned,
  portalHost,
}: MealScannerModalProps) => {
  const { t } = useTranslation('meal');
  const [permission, requestPermission] = useCameraPermissions();
  const scannedRef = useRef(false);

  const handleBarCodeScanned = ({ data }: { data: string }) => {
    if (scannedRef.current) return;
    scannedRef.current = true;
    // Dismiss the scanner first, then present the next modal on a later tick so
    // the iOS modal transitions don't overlap (see MODAL_TRANSITION_DELAY_MS).
    onClose?.();
    setTimeout(() => onBarcodeScanned?.(data), MODAL_TRANSITION_DELAY_MS);
  };

  return (
    <Drawer open={true} onOpenChange={() => onClose()}>
      <DrawerContent>
        <DrawerHeader onClose={onClose}>
          <Text className="text-lg font-sora-semibold text-text-primary">
            {t('scanner.title')}
          </Text>
        </DrawerHeader>
        <DrawerBody className="px-5 pb-5">
          {!permission?.granted ? (
            <View className="flex-1 items-center justify-center gap-4 p-6">
              <Text className="text-sm text-text-muted text-center">
                {t('scanner.permissionMessage')}
              </Text>
              <Button onPress={requestPermission}>
                <Text>{t('scanner.enableCamera')}</Text>
              </Button>
            </View>
          ) : (
            <CameraView
              style={{ flex: 1 }}
              zoom={0.1}
              facing="back"
              onBarcodeScanned={handleBarCodeScanned}
              barcodeScannerSettings={{
                barcodeTypes: ['ean13', 'ean8', 'upc_a', 'upc_e'],
              }}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
